/**
 * History Page - User's Trip History
 *
 * Displays a paginated list of the user's past kit generations.
 * Requires authentication to view.
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useHistory } from '../hooks/useHistory';

/**
 * Format a price with commas for thousands
 */
function formatPrice(price: number): string {
  return `£${price.toLocaleString()}`;
}

/**
 * Format a date in a human-readable format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function History() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { kits, total, isLoading, error, hasMore, loadMore, refresh } = useHistory();

  // Update document title
  useEffect(() => {
    document.title = 'Trip History | NFA';
  }, []);

  // Show login prompt if not authenticated
  if (!authLoading && !isAuthenticated) {
    return (
      <main className="flex-1 px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto text-center">
          <svg className="w-16 h-16 text-zinc-700 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h1 className="text-2xl font-bold text-white mb-4">Trip History</h1>
          <p className="text-zinc-400 mb-6">
            Sign in to view your trip history and revisit your past kit recommendations.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
          >
            Sign In
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
      </main>
    );
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <main className="flex-1 px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse text-center py-12">
            <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-zinc-400">Loading...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 px-4 sm:px-6 py-8 sm:py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Trip History</h1>
          <p className="text-zinc-400">
            {total > 0 ? `${total} trips saved` : 'Your past kit recommendations will appear here.'}
          </p>
        </div>

        {/* Loading */}
        {isLoading && kits.length === 0 && (
          <div className="text-center py-12">
            <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-zinc-400">Loading your history...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
            <p className="text-red-400 mb-3">Error: {error}</p>
            <button
              onClick={() => refresh()}
              className="text-sm text-orange-500 hover:text-orange-400 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && kits.length === 0 && (
          <div className="text-center py-12 bg-zinc-900 rounded-lg">
            <svg className="w-16 h-16 text-zinc-700 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="text-lg font-medium text-white mb-2">No trips yet</h3>
            <p className="text-zinc-400 mb-6">
              Build your first kit and it will appear here.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
            >
              Build Your First Kit
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        )}

        {/* History list */}
        {!error && kits.length > 0 && (
          <div className="space-y-4">
            {kits.map(kit => (
              <Link
                key={kit.id}
                to={`/kit/${kit.slug || kit.kit_id}`}
                className="block bg-zinc-900 hover:bg-zinc-800 rounded-lg p-4 transition-colors group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white group-hover:text-orange-400 transition-colors truncate">
                      {kit.query}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-zinc-400">
                      <span>{kit.item_count} items</span>
                      <span>{kit.total_weight_kg.toFixed(1)} kg</span>
                      <span>{formatPrice(kit.total_price_gbp)}</span>
                    </div>
                  </div>
                  <div className="text-sm text-zinc-500 shrink-0">
                    {formatDate(kit.created_at)}
                  </div>
                </div>
              </Link>
            ))}

            {/* Load more button */}
            {hasMore && (
              <div className="text-center pt-4">
                <button
                  onClick={() => loadMore()}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-6 py-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      Load More
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Stats footer */}
            <div className="border-t border-zinc-800 pt-6 mt-8">
              <div className="flex items-center justify-between text-sm text-zinc-400">
                <span>Showing {kits.length} of {total} trips</span>
                <Link
                  to="/"
                  className="text-orange-500 hover:text-orange-400 transition-colors"
                >
                  Build New Kit
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
